import type { CSSProperties } from 'react'

/**
 * Как выглядит косметика — по коду вещи из config/shop.php на сервере.
 *
 * Каталог (цены, названия) присылает сервер, а отрисовка живёт здесь: вещь
 * без отрисовки продать нельзя, поэтому новый код заводится в обоих местах.
 * Незнакомый код — вид по умолчанию, а не ошибка: старый клиент не должен
 * ломаться от нового товара.
 */

type DiceSkin = {
  /** Грань: цвет или градиент. */
  face: string
  edge: string
  pip: string
}

/**
 * Кубики — «материалы», а не цвета темы: золотые кубики золотые и в тёмной
 * теме, иначе вещь выглядела бы у каждого по-своему.
 */
const DICE: Record<string, DiceSkin> = {
  dice_night: { face: '#23272c', edge: '#3b4148', pip: '#f2d27a' },
  dice_ivory: { face: '#f6f0e1', edge: '#d8cdb4', pip: '#3b2f22' },
  dice_ruby: { face: 'linear-gradient(145deg, #c8384c, #8f1b2d)', edge: '#6e1322', pip: '#fff4f4' },
  dice_gold: { face: 'linear-gradient(145deg, #f8df86, #c8961f)', edge: '#9b7313', pip: '#3a2904' },
}

/**
 * Переменные для кубиков: их читают стили граней и точек (.die__face, .pip).
 * Без скина — пусто, и остаются цвета темы.
 */
export function diceStyle(code: string | null | undefined): CSSProperties {
  const skin = code === null || code === undefined ? undefined : DICE[code]

  if (skin === undefined) return {}

  return {
    '--die-face': skin.face,
    '--die-edge': skin.edge,
    '--die-pip': skin.pip,
  } as CSSProperties
}

/**
 * Значки на фишках — контур в координатах фишки (радиус 0.44, центр в нуле).
 *
 * Значок, а не перекраска: цвет фишки — это игрок, и менять его нельзя.
 * Размер — чтобы значок читался на телефоне и не спорил с кантом.
 */
const EMBLEMS: Record<string, string> = {
  token_star:
    'M0 -0.22 L0.059 -0.071 L0.219 -0.061 L0.095 0.041 L0.135 0.196 L0 0.11 L-0.135 0.196 L-0.095 0.041 L-0.219 -0.061 L-0.059 -0.071 Z',
  token_heart:
    'M0 0.2 C-0.3 0.02 -0.22 -0.2 -0.09 -0.2 C-0.04 -0.2 0 -0.16 0 -0.12 C0 -0.16 0.04 -0.2 0.09 -0.2 C0.22 -0.2 0.3 0.02 0 0.2 Z',
  token_diamond: 'M0 -0.23 L0.17 0 L0 0.23 L-0.17 0 Z',
  token_crown: 'M-0.21 0.14 L-0.21 -0.1 L-0.1 0.01 L0 -0.17 L0.1 0.01 L0.21 -0.1 L0.21 0.14 Z',
}

export function emblemPath(code: string | null | undefined): string | null {
  return code === null || code === undefined ? null : (EMBLEMS[code] ?? null)
}

type Frame = { ring: string; edge: string }

/**
 * Рамки вокруг аватара: широкое кольцо материала и тонкая тёмная кромка
 * внутри. Кромка — чтобы рамка отделялась от картинки любого цвета: без неё
 * золото на жёлтой аватарке сливалось в одно пятно.
 */
const FRAMES: Record<string, Frame> = {
  frame_silver: { ring: '#cfd6dd', edge: '#6b7680' },
  frame_emerald: { ring: '#2fa36b', edge: '#14553a' },
  frame_ruby: { ring: '#c8384c', edge: '#6e1322' },
  frame_gold: { ring: '#e7bf4f', edge: '#8a6410' },
}

export function frameOf(code: string | null | undefined): Frame | null {
  return code === null || code === undefined ? null : (FRAMES[code] ?? null)
}
